/**
 * As peças de confete, geradas por conta.
 *
 * Era um array de 120 peças fixo dentro do `Comemoracao.tsx` (13/10). Saiu para
 * cá em 14/10, quando a festa por marca passou a precisar de 80 coloridas e 160
 * douradas: a quantidade e a paleta viraram parâmetro, e a geração virou função
 * pura — testável pelo `node --test`, que não compila JSX.
 *
 * **Nada é sorteado.** A posição, o atraso, a duração e o giro saem de
 * multiplicação com números primos sobre o índice. Dois motivos:
 *
 *  - `Math.random()` a cada repintura geraria peças novas no meio da queda, e o
 *    confete "piscaria" a cada snapshot que chega;
 *  - o mesmo índice sempre produz a mesma peça, então a tela é a mesma em todo
 *    telão — e o que se vê num ensaio é o que a sala vai ver.
 *
 * Os primos (37, 173, 211, 97) espalham os valores sem repetir padrão visível
 * em 160 peças: com números redondos elas saem em colunas e em cascata, e a
 * queda deixa de parecer confete.
 */

/** A paleta da festa. A dourada é a da meta — a colorida, de todo o resto. */
export type Paleta = 'colorida' | 'dourada'

/* Cinco cores em cada: a marca da Lunelli (dourado e osso), o verde do painel e
   dois neutros. A dourada usa cinco tons de ouro, do claro ao queimado, porque
   um dourado só, repetido 160 vezes, lê como papel picado amarelo. */
const CORES: Record<Paleta, string[]> = {
  colorida: ['#d4af37', '#e8e3d3', '#0ca30c', '#b07646', '#aaaeb5'],
  dourada: ['#d4af37', '#f1d272', '#b8860b', '#ffe9a8', '#8c6a1f'],
}

export type PecaDeConfete = {
  id: number
  /** Posição horizontal, em % da tela. */
  esquerda: number
  atrasoMs: number
  duracaoMs: number
  /** Quanto a peça gira ao cair, em graus. Negativo gira para o outro lado. */
  giro: number
  cor: string
  /** Largura em px; a altura é 1,8× ela, que é o que dá a forma de papel. */
  largura: number
}

/**
 * Gera as peças. `quantidade` fora de 1..400 é cortada para o intervalo: 400
 * peças já são 400 nós animados, e o telão é um datashow velho.
 */
export function pecasDeConfete(quantidade: number, paleta: Paleta = 'colorida'): PecaDeConfete[] {
  const total = Number.isFinite(quantidade) ? Math.min(Math.max(Math.trunc(quantidade), 0), 400) : 0
  const cores = CORES[paleta] ?? CORES.colorida

  return Array.from({ length: total }, (_, indice) => ({
    id: indice,
    esquerda: (indice * 37) % 100,
    atrasoMs: (indice * 173) % 2600,
    duracaoMs: 3200 + ((indice * 211) % 2400),
    giro: ((indice * 97) % 360) - 180,
    /* `!` não: índice de módulo sempre cai dentro, mas o tipo não sabe disso e
       `?? ` deixa explícito o que acontece com paleta vazia. */
    cor: cores[indice % cores.length] ?? '#d4af37',
    largura: 7 + (indice % 4) * 2,
  }))
}

/**
 * Uma faísca de vela jupiter — a fonte de faísca que fica de cada lado do nome
 * da marca na comemoração da META (pedido de 14/10).
 *
 * Mesma disciplina do confete: determinística, sem `Math.random()`, e animada
 * por CSS. A faísca sai da ponta da vela num ÂNGULO e com um ALCANCE, e é a
 * variação dos dois que faz o jorro parecer um leque em vez de um chuveiro.
 */
export type Faisca = {
  id: number
  /** Graus a partir da vertical. Negativo joga para a esquerda. */
  angulo: number
  /** Quanto ela sobe antes de apagar, em px. */
  alcance: number
  atrasoMs: number
  duracaoMs: number
  tamanho: number
}

/**
 * Quantas faíscas cada vela joga.
 *
 * Eram 12, e num frame congelado (a captura de 14/10, e a foto que alguém da
 * sala tira) o jorro aparecia ralo: com atraso de até 900 ms e voo de ~1 s, só
 * parte delas está no ar a cada instante. Dezoito mantêm o leque cheio sem
 * passar de 36 nós animados na tela toda.
 */
export const FAISCAS_POR_VELA = 18

/**
 * As faíscas de uma vela. `lado` inclina o leque todo para fora do nome: a vela
 * da esquerda jorra para a esquerda, a da direita para a direita — apontadas
 * para dentro, as duas cobririam o nome da marca, que é o que a tela existe
 * para mostrar.
 */
export function faiscasDaVela(lado: 'esquerda' | 'direita', semente = 0): Faisca[] {
  const sentido = lado === 'esquerda' ? -1 : 1
  /* `semente` desloca o gerador (16/10): a SUPER META põe mais de uma vela do
     mesmo lado, e com o mesmo jorro em duas delas o olho vê cópia, não fogo.
     Com `0` o resultado é EXATAMENTE o de antes — a festa da marca não muda. */
  const desvio = Math.trunc(semente)
  return Array.from({ length: FAISCAS_POR_VELA }, (_, indice) => {
    const n = indice + desvio * 7
    return {
      id: indice,
      /* Leque de ~48° (de -4° a +44° a partir da vertical), virado para fora. */
      angulo: sentido * (4 + ((n * 29) % 44)),
      alcance: 70 + ((n * 53) % 90),
      /* O atraso espalha por toda a duração do voo: é o que faz o jorro ser
         contínuo em vez de sair em levas. */
      atrasoMs: (n * 137) % 1100,
      duracaoMs: 700 + ((n * 71) % 500),
      tamanho: 3 + (n % 3),
    }
  })
}
